import { useEffect, useRef } from 'react';
import { bluetoothButtonService } from '../services/bluetoothButtonService';

// Supported trigger keys for Bluetooth remotes and presentation clickers
const TRIGGER_KEYS = [
  'Enter',     // Camera shutter remotes (AB Shutter 3, etc.)
  'PageUp',    // Presentation clickers (Logitech R400, etc.)
  'PageDown',  // Presentation clickers
  ' ',         // Space bar (some remotes)
];

/**
 * Custom hook for detecting Bluetooth remote button presses.
 *
 * Supports:
 * - Camera shutter remotes (AB Shutter 3) - sends Enter key
 * - Presentation clickers (Logitech R400, Spotlight) - sends PageUp/PageDown
 * - Other HID devices that send Space key
 *
 * @param {boolean} enabled - Whether Bluetooth button detection is active
 * @param {function} onTrigger - Callback function to execute when button is pressed
 * @returns {object} - { isSupported: true } (always supported since it's just keyboard events)
 */
export const useBluetoothButton = (enabled, onTrigger) => {
  // Use ref for callback to avoid restarting listener on callback changes
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    console.log('Bluetooth button hook:', { enabled });

    if (!enabled) {
      console.log('Bluetooth button detection disabled');
      return;
    }

    const handleKeyDown = (event) => {
      // Only handle supported trigger keys
      if (!TRIGGER_KEYS.includes(event.key)) return;

      // Ignore if typing in an input field or textarea
      const tagName = event.target.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        console.log('Bluetooth button: Ignoring key in input field');
        return;
      }

      // Check cooldown
      if (!bluetoothButtonService.canTriggerNow()) {
        console.log('Bluetooth button: Cooldown active, ignoring');
        return;
      }

      console.log(`Bluetooth button: ${event.key} key detected, triggering action`);

      // Prevent default behavior (form submission, etc.)
      event.preventDefault();

      // Update cooldown timestamp
      bluetoothButtonService.setLastTriggerTime();

      // Trigger haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }

      // Execute callback via ref
      onTriggerRef.current?.();
    };

    console.log('Setting up Bluetooth button (Enter key) listener');
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      console.log('Removing Bluetooth button listener');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]); // Only restart when enabled changes

  return {
    isSupported: true, // Always supported - it's just keyboard events
  };
};
